import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Item 100: Development-only accessibility audit hook.
 * Checks for common a11y issues and logs warnings to console.
 * Only runs in development mode.
 */
export function useA11yAudit() {
  const location = useLocation();

  useEffect(() => {
    // Only run in development
    if (import.meta.env.PROD) return;

    // Defer to not block rendering
    const timer = setTimeout(() => {
      const warnings: string[] = [];

      // 1. Check all images have alt text
      const images = document.querySelectorAll('img');
      images.forEach((img) => {
        if (!img.hasAttribute('alt')) {
          warnings.push(
            `[A11y] Image missing alt attribute: ${img.src.substring(0, 80)}`
          );
        }
      });

      // 2. Check all buttons have accessible names
      const buttons = document.querySelectorAll('button');
      buttons.forEach((btn) => {
        const hasText = btn.textContent?.trim();
        const hasAriaLabel = btn.getAttribute('aria-label');
        const hasAriaLabelledBy = btn.getAttribute('aria-labelledby');
        const hasTitle = btn.getAttribute('title');
        if (!hasText && !hasAriaLabel && !hasAriaLabelledBy && !hasTitle) {
          warnings.push(
            `[A11y] Button missing accessible name: ${btn.outerHTML.substring(0, 100)}`
          );
        }
      });

      // 3. Check form inputs have labels
      const inputs = document.querySelectorAll('input, textarea, select');
      inputs.forEach((input) => {
        const el = input as HTMLInputElement;
        const id = el.id;
        const type = el.type;

        // Skip hidden and submit inputs
        if (type === 'hidden' || type === 'submit') return;

        const hasLabel = id && document.querySelector(`label[for="${id}"]`);
        const hasAriaLabel = el.getAttribute('aria-label');
        const hasAriaLabelledBy = el.getAttribute('aria-labelledby');
        const hasPlaceholder = el.getAttribute('placeholder');
        const wrappedInLabel = el.closest('label');

        if (!hasLabel && !hasAriaLabel && !hasAriaLabelledBy && !wrappedInLabel) {
          warnings.push(
            `[A11y] Form input missing label: <${el.tagName.toLowerCase()} type="${type}" ${id ? `id="${id}"` : ''} ${hasPlaceholder ? `placeholder="${hasPlaceholder}"` : ''}>`
          );
        }
      });

      // 4. Check for missing landmark roles
      const main = document.querySelector('main, [role="main"]');
      if (!main) {
        warnings.push('[A11y] Page missing <main> landmark');
      }

      const nav = document.querySelector('nav, [role="navigation"]');
      if (!nav) {
        warnings.push('[A11y] Page missing <nav> landmark');
      }

      // 5. Check heading hierarchy
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      let lastLevel = 0;
      headings.forEach((heading) => {
        const level = parseInt(heading.tagName[1]);
        if (level > lastLevel + 1 && lastLevel > 0) {
          warnings.push(
            `[A11y] Heading level skipped: h${lastLevel} → h${level} ("${heading.textContent?.trim().substring(0, 50)}")`
          );
        }
        lastLevel = level;
      });

      // 6. Check for missing lang attribute
      if (!document.documentElement.lang) {
        warnings.push('[A11y] Missing lang attribute on <html>');
      }

      // Log results
      if (warnings.length > 0) {
        console.group(
          `%c♿ A11y Audit (${location.pathname}) — ${warnings.length} issue${warnings.length !== 1 ? 's' : ''}`,
          'color: #f59e0b; font-weight: bold'
        );
        warnings.forEach((w) => console.warn(w));
        console.groupEnd();
      } else {
        console.log(
          `%c♿ A11y Audit (${location.pathname}) — No issues found ✓`,
          'color: #22c55e; font-weight: bold'
        );
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [location.pathname]);
}
